/**
 * What each tier actually gets.
 *
 * Kept in one place and as plain data, because entitlements drift: a limit
 * written into a marketing page, a different one checked in a route, and a
 * third implied by a UI that hides a button. When those disagree, a paying
 * customer hits a wall they were told they had bought past.
 *
 * The free tier is deliberately generous where the market leader is stingy --
 * unlimited medications is the wedge -- and limited on the things that cost us
 * money per use.
 */

export type Tier = 'free' | 'plus' | 'family' | 'org';

export type Channel = 'push' | 'whatsapp' | 'sms' | 'voice' | 'email';

export interface Entitlements {
  tier: Tier;
  /** null means unlimited. */
  maxMedications: number | null;
  maxCaregiversPerPatient: number | null;
  maxPatients: number | null;
  /**
   * Phones signed in to the account at once — the person's OWN plan, not a
   * record's: a caregiver on Free helping a mother on Family still has two.
   * Counted as phones not stopped and heard from within 30 days, and only
   * when a new phone joins: one already registered is never turned away.
   */
  maxDevices: number | null;
  /** How far back adherence history can be read. null means all of it. */
  historyDays: number | null;
  channels: readonly Channel[];
  /**
   * Whether anyone besides the person is told about their medicines: a
   * missed, late, skipped or deferred dose, a phone that cannot ring, a
   * refill, an expiry, an appointment.
   *
   * Off on Free (the owner, 20 October 2026): on Free only the person
   * whose record it is hears — `recordRinger`, which is whoever set up a
   * record nobody has on their own phone. A caregiver may still be added,
   * and still sees the record; they are not told. Two things are not
   * "telling a caregiver" and stay on every plan: a child's "I took it"
   * that a grown-up confirms, and a hands-on helper's phone ringing the
   * reminder itself. A care home's staff are never affected.
   *
   * Code, not a console column: what the notifier does is decided here.
   */
  caregiverAlerts: boolean;
  canShareReports: boolean;
  canScanPrescriptions: boolean;
  canUseVoiceEscalation: boolean;
  /** Organisations only. */
  canManageOrganisation: boolean;
}

const FREE: Entitlements = {
  tier: 'free',
  // Unlimited medications is the whole competitive argument. Medisafe cut
  // its free tier to two in January 2026; matching that would be pointless.
  maxMedications: null,
  maxCaregiversPerPatient: 1,
  maxPatients: 2,
  // Their own phone and one more: a new handset before the old one is
  // wiped, or a tablet. Not a family's worth of phones on one account.
  maxDevices: 2,
  historyDays: 30,
  // Push only. Everything else costs money per message.
  channels: ['push'],
  caregiverAlerts: false,
  canShareReports: false,
  canScanPrescriptions: false,
  canUseVoiceEscalation: false,
  canManageOrganisation: false,
};

const PLUS: Entitlements = {
  ...FREE,
  tier: 'plus',
  maxCaregiversPerPatient: 5,
  maxPatients: 3,
  maxDevices: 5,
  historyDays: null,
  channels: ['push', 'whatsapp', 'sms', 'email'],
  caregiverAlerts: true,
  canShareReports: true,
  canScanPrescriptions: true,
};

const FAMILY: Entitlements = {
  ...PLUS,
  tier: 'family',
  maxCaregiversPerPatient: 10,
  maxPatients: 6,
  maxDevices: 10,
  /*
   * No voice, and no voice escalation, until there is a voice vendor.
   *
   * `notify/notifier.ts` registers an UnconfiguredNotifier for the channel:
   * nothing rings, and nothing ever has. This entitlement was what the
   * paywall read to promise "a phone call when a dose is missed", and that
   * promise reached Play in 168 countries before anybody noticed.
   *
   * An entitlement is a promise. Granting one the system cannot keep is
   * worse than not selling the tier — a family who paid for a call that
   * never comes learns it on the day it mattered. Turn these back on in
   * the same change that ships a working voice notifier, not before.
   */
  channels: ['push', 'whatsapp', 'sms', 'email'],
  canUseVoiceEscalation: false,
};

const ORG: Entitlements = {
  ...FAMILY,
  tier: 'org',
  maxCaregiversPerPatient: null,
  // Bounded by the contracted seat count instead, which lives on the
  // organisation rather than the tier.
  maxPatients: null,
  // A care home's shared ward phones are its own business.
  maxDevices: null,
  canManageOrganisation: true,
};

const BY_TIER: Record<Tier, Entitlements> = {
  free: FREE,
  plus: PLUS,
  family: FAMILY,
  org: ORG,
};

export function entitlementsFor(tier: Tier): Entitlements {
  return BY_TIER[tier];
}

/**
 * Is a subscription currently worth honouring?
 *
 * `past_due` deliberately still counts. Cutting someone's caregiver alerts off
 * the moment a card fails is the wrong response to a payment problem when the
 * product's job is telling a family that their mother missed her heart
 * medication. Dunning belongs in billing, not in the escalation path.
 */
export function tierFromSubscription(
  subscription:
    | { tier: Tier; status: string; store?: string | null; currentPeriodEnd?: number | null }
    | null
    | undefined,
  now?: number,
): Tier {
  if (!subscription) return 'free';
  if (now !== undefined && complimentaryLapsed(subscription, now)) return 'free';
  const honoured = ['active', 'trialing', 'past_due'];
  return honoured.includes(subscription.status) ? subscription.tier : 'free';
}

/**
 * The `store` of a subscription nobody paid for — given from the console,
 * for a referral, a trial, an apology.
 *
 * A store slug like `google` or `razorpay`, so it lives in the same column
 * and every reader of a subscription already understands it. What makes it
 * different is that nobody will ever send a notification about it: no store
 * is going to say "expired". So its end date is the whole of its truth, and
 * `complimentaryLapsed` reads it.
 */
export const COMPLIMENTARY = 'complimentary';

/**
 * Has a complimentary grant run out, whatever its status still says?
 *
 * A store-billed row is never judged by its date here. Google's grace
 * period and Apple's billing retry both outlive `currentPeriodEnd`, and
 * the store's own notification is what ends those — reading the date
 * would cut somebody off mid-retry, which is the one thing billing must
 * not do. A complimentary grant has no store to tell us, so the date is
 * the answer, and it is checked on every read rather than trusted to the
 * hourly sweep: an hour of Family after a trial ends is harmless, but a
 * sweep that silently stops running would be a gift for ever.
 *
 * `currentPeriodEnd` is epoch SECONDS, as every store sends it; `now` is
 * milliseconds, like everything else here.
 */
export function complimentaryLapsed(
  subscription: { store?: string | null; currentPeriodEnd?: number | null },
  now: number,
): boolean {
  return (
    subscription.store === COMPLIMENTARY &&
    subscription.currentPeriodEnd != null &&
    subscription.currentPeriodEnd * 1000 <= now
  );
}

export interface LimitCheck {
  allowed: boolean;
  limit: number | null;
  used: number;
  /** The lowest tier that would permit this. */
  upgradeTo?: Tier;
}

const ORDER: readonly Tier[] = ['free', 'plus', 'family', 'org'];

/**
 * Check a count against a limit, and say which tier would lift it.
 *
 * Naming the tier matters: "you have reached your limit" is a dead end, while
 * "Plus allows 5" is an answer.
 */
export function checkLimit(
  tier: Tier,
  used: number,
  select: (e: Entitlements) => number | null,
): LimitCheck {
  const limit = select(entitlementsFor(tier));
  if (limit === null || used < limit) return { allowed: true, limit, used };

  const upgrade = ORDER.slice(ORDER.indexOf(tier) + 1).find((t) => {
    const next = select(entitlementsFor(t));
    return next === null || next > limit;
  });

  return { allowed: false, limit, used, ...(upgrade ? { upgradeTo: upgrade } : {}) };
}

/** Clamp a requested history window to what the tier allows. */
export function clampHistoryDays(tier: Tier, requestedDays: number): number {
  const allowed = entitlementsFor(tier).historyDays;
  return allowed === null ? requestedDays : Math.min(requestedDays, allowed);
}

export function channelsFor(tier: Tier): readonly Channel[] {
  return entitlementsFor(tier).channels;
}
